<script>
    import * as Utils from '@js/modules/utils';
    import I18n from '@js/modules/translations';

    import SearchManager from '@js/managers/searchManager';
    import {searchStore} from '@js/stores/searchStore';
    import {mapStore} from '@js/stores/mapStore';

    import SearchResultCard from '@js/components/search/result/SearchResultCard.svelte';

    const {
        onClearFilters,
        onSearchArea
    } = $props();

    const _handleRideHover = (rideId, isHover) => {
        // A tap's synthetic hover must change nothing, or iOS cancels the tap on the result
        if (!Utils.canHover()) {
            return;
        }

        searchStore.highlightedRideId = isHover ? rideId : undefined;

        if (!mapStore.map) {
            return;
        }

        mapStore.map.highlightRide(searchStore.highlightedRideId, {
            openPopup: true,
            showTrack: true
        });
    };

    // Server order (relevance or the chosen sort), narrowed to what the map shows
    const visibleRides = $derived($searchStore.ridesData.filter((ride) => $searchStore.visibleRideIds.includes(ride.id)));

    const isQueryMode = $derived(Utils.isPresent($searchStore.query) && !$searchStore.ridesLoadedFromMap);

    const hiddenCount = $derived(isQueryMode ? $searchStore.ridesData.length - visibleRides.length : 0);

    const hasFilters = $derived(Object.values($searchStore.selectedFilters || {}).some(Boolean));

    // "around <place>" for a place; a picked route or a wordless search ("around me", map area) shows its label as is
    const placeLabel = $derived($searchStore.queryDisplay && $searchStore.query !== '*' && !$searchStore.pickedRideId ? $searchStore.queryDisplay : null);
</script>

<section aria-label={I18n.t('js.search.index.list_label')}>
    {#if isQueryMode && $searchStore.ridesData.length}
        <div class="flex items-baseline justify-between flex-wrap gap-x-3 gap-y-1 mb-3"
             role="status">
            <p class="m-0 text-base text-gray-900">
                <span class="font-bold">
                    {I18n.t('js.search.index.results.count', {count: $searchStore.totalCount || $searchStore.ridesData.length})}
                </span>
                {#if placeLabel}
                    <span class="text-gray-600">· {I18n.t('js.search.index.results.around', {place: placeLabel})}</span>
                {:else if $searchStore.queryDisplay}
                    <span class="text-gray-600">· {$searchStore.queryDisplay}</span>
                {/if}
            </p>

            {#if hiddenCount > 0}
                <p class="m-0 text-sm text-gray-600">
                    {I18n.t('js.search.index.results.visible', {visible: visibleRides.length, total: $searchStore.ridesData.length})}
                    <button type="button"
                            class="ms-1 font-medium text-primary-800 underline underline-offset-2 hover:text-primary-900 cursor-pointer"
                            onclick={() => SearchManager.fitResults()}>
                        {I18n.t('js.search.index.show_all')}
                    </button>
                </p>
            {/if}
        </div>
    {/if}

    {#if $searchStore.isSearching && !$searchStore.ridesData.length}
        <div role="status"
             class="space-y-3 animate-pulse">
            {#each [1, 2, 3, 4] as placeholder (placeholder)}
                <div class="flex h-28 bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div class="w-32 sm:w-40 bg-gray-200"></div>
                    <div class="flex-1 p-3 space-y-2">
                        <div class="h-4 w-20 bg-gray-200 rounded-full"></div>
                        <div class="h-4 w-3/4 bg-gray-200 rounded"></div>
                        <div class="h-4 w-1/2 bg-gray-200 rounded"></div>
                    </div>
                </div>
            {/each}
            <span class="sr-only">{I18n.t('js.a11y.loading')}</span>
        </div>
    {:else if !$searchStore.ridesData.length && Utils.isPresent($searchStore.query)}
        <div class="my-8 mx-auto max-w-md text-base text-center text-gray-700"
             role="status">
            <p class="m-0">
                {hasFilters ? I18n.t('js.search.index.no_results.title_filters') : I18n.t('js.search.index.no_results.title')}
                {#if $searchStore.queryDisplay && $searchStore.query !== '*'}
                    <span class="font-bold">{$searchStore.queryDisplay}</span>
                {/if}
            </p>

            <div class="flex items-center justify-center flex-wrap gap-2 mt-4">
                {#if hasFilters}
                    <button type="button"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-100 cursor-pointer"
                            onclick={onClearFilters}>
                        {I18n.t('js.search.index.more_filters.clear')}
                    </button>
                {/if}

                <button type="button"
                        class="px-4 py-2 text-sm font-medium text-white bg-primary-700 border border-primary-700 rounded-full hover:bg-primary-800 cursor-pointer"
                        onclick={onSearchArea}>
                    {I18n.t('js.search.index.empty.widen')}
                </button>
            </div>
        </div>
    {/if}

    <ul class="space-y-3">
        {#each visibleRides as ride (ride.id)}
            <li id={`search-result-${ride.id}`}
                onmouseenter={_handleRideHover.bind(this, ride.id, true)}
                onmouseleave={_handleRideHover.bind(this, ride.id, false)}>
                <SearchResultCard ride={ride}
                                  isHighlighted={$searchStore.highlightedRideId === ride.id || $searchStore.selectedRideId === ride.id}/>
            </li>
        {/each}
    </ul>
</section>
